import { BaseTableDefinitionStore } from '@/tableDefinitionEditor/baseTableDefinitionStore';
import {
  ADD_TO_TREE_PSEUDO_RULE,
  CREATE_TREE_PSEUDO_RULE,
} from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import {
  ITEM_TABLE_DEFINITION_CANCELLATION_GROUP,
  ITEM_TABLE_FETCH_CANCELLATION_GROUP,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import { TableTypeEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { ColumnRule } from '@/tableDefinitionEditor/columnRules/columnRule.constants';

const DefaultType = TableTypeEnum.ITEM;
export class ItemTableDefinitionStore extends BaseTableDefinitionStore {
  defaultName = '';
  static readonly storeName = 'sqItemTableDefinitionStore';

  initialize(): void {
    this.setupState(DefaultType);
  }

  get name(): string {
    return this.state.get('name');
  }

  getMaterializedTableCancellationGroup(): string {
    return ITEM_TABLE_FETCH_CANCELLATION_GROUP;
  }

  getTableDefinitionCancellationGroup(): string {
    return ITEM_TABLE_DEFINITION_CANCELLATION_GROUP;
  }

  getRulesNotAllowed(): ColumnRule[] {
    const notAllowedInItemTables: ColumnRule[] = [
      SeeqNames.MaterializedTables.Rules.FormulaCreator,
      SeeqNames.MaterializedTables.Rules.AssetCreator,
      SeeqNames.MaterializedTables.Rules.TreePathCreator,
      SeeqNames.MaterializedTables.Rules.ScalarCreator.BaseScalarCreator,
      SeeqNames.MaterializedTables.Rules.StringScalarCreator,
      SeeqNames.MaterializedTables.Rules.NumericScalarCreator,
      CREATE_TREE_PSEUDO_RULE,
      ADD_TO_TREE_PSEUDO_RULE,
      SeeqNames.MaterializedTables.Rules.Ancestor,
      SeeqNames.MaterializedTables.Rules.EventAssetCreator,
    ];

    return this.tableType === TableTypeEnum.ITEM
      ? notAllowedInItemTables.concat([
          SeeqNames.MaterializedTables.Rules.ItemSearch,
          SeeqNames.MaterializedTables.Rules.PathSearch,
        ])
      : notAllowedInItemTables;
  }

  override handlers = {
    ITEM_TABLE_DEFINITION_SET_SUBSCRIBER_ID: this.setSubscriberId,
    ITEM_TABLE_DEFINITION_SET_SHOULD_SEARCH_FOR_ITEMS: this.setShouldSearchForItems,
    ITEM_TABLE_DEFINITION_SET_NAME: this.setName,
    ITEM_TABLE_DEFINITION_SET_SEARCH_TYPE_AI: this.setSearchTypeAI,
    ITEM_TABLE_DEFINITION_SET_DESCRIPTION: this.setDescription,
    ITEM_TABLE_DEFINITION_SET_BATCH_ACTION: this.setBatchAction,
    ITEM_TABLE_DEFINITION_SET_DO_TABLE_RELOAD: this.setDoTableReload,
    ITEM_TABLE_DEFINITION_SET_TABLE_DEFINITION: this.setTableDefinition,
    ITEM_TABLE_DEFINITION_SET_ITEM_FINDER: this.setItemFinder,
    ITEM_TABLE_DEFINITION_SET_MATERIALIZED_TABLE: this.setMaterializedTable,
    ITEM_TABLE_DEFINITION_SET_EVALUATION_RESULTS: this.setEvaluationResults,
    ITEM_TABLE_DEFINITION_MERGE_EVALUATION_RESULTS: this.mergeEvaluationResults,
    ITEM_TABLE_DEFINITION_RESET: this.reset,
    ITEM_TABLE_DEFINITION_ADD_PENDING_TABLE_ID: this.addPendingTableId,
    ITEM_TABLE_DEFINITION_REMOVE_PENDING_TABLE_ID: this.removePendingTableId,
    ITEM_TABLE_DEFINITION_SET_CREATED_ITEM_FINDER_ID: this.setCreatedItemFinderId,
    ITEM_TABLE_DEFINITION_SET_ALLOW_HIDDEN_COLUMNS_AS_INPUTS: this.setAllowHiddenColumnsAsInputs,
    ITEM_TABLE_DEFINITION_SET_AG_GRID_API: this.setAgGridApi,
    ITEM_TABLE_DEFINITION_SET_ROW_DATA_IS_LOADING: this.setRowDataIsLoading,
    ITEM_TABLE_DEFINITION_SET_IS_LOADING_DEFINITION: this.setIsLoadingDefinition,
    REQUEST_SET_PROGRESS: this.requestSetProgress,
    ITEM_TABLE_DEFINITION_SET_SELECTED_COLUMNS: this.setSelectedColumns,
    ITEM_TABLE_DEFINITION_SET_SHOW_CONFIGURE_COLUMN: this.setShowConfigureColumn,
    ITEM_TABLE_DEFINITION_SET_SCROLL_TO_COLUMN_ID: this.setScrollToColumnId,
    ITEM_ADD_ROWS_TO_FILTER_OUT: this.addRowItemIdsInsertedInGroupLevel,
    ITEM_CLEAR_ROW_IDS: this.clearRowItemIdsInsertedInGroupLevel,
    ITEM_TABLE_DEFINITION_SET_SHOW_CREATE_TEMPLATE: this.setShowCreateTemplate,
    ITEM_TABLE_DEFINITION_SET_IS_APPLYING_TEMPLATE: this.setIsApplyingTemplate,
    ITEM_TABLE_DEFINITION_SET_TEMPLATE_INPUT_TO_SELECT: this.setTemplateInputToSelect,
    ITEM_TABLE_DEFINITION_SET_COLUMN_ID_TO_BE_USED_AS_TEMPLATE_INPUT: this.setColumnIdToBeUsedAsTemplateInput,
    ITEM_TABLE_DEFINITION_SET_TEMPLATE_COLUMNS: this.setTemplateColumns,
    ITEM_TABLE_DEFINITION_SET_IS_EDITING_TEMPLATE: this.setIsEditingTemplate,
    ITEM_TABLE_DEFINITION_SET_EDIT_TEMPLATE_REMOVED_OUTPUT_BY_COLUMN_ID: this.setEditTemplateRemovedOutputByColumnId,
    ITEM_TABLE_DEFINITION_SET_COLUMNS: this.setColumns,
    ITEM_TABLE_DEFINITION_SET_HIERARCHIES_USING_COLUMN: this.setHierarchiesUsingThisColumnAsGroup,
    ITEM_TABLE_DEFINITION_SET_COLUMN_TO_DELETE: this.setColumnToDelete,
    ITEM_TABLE_DEFINITION_SET_SHOW_RETIRE_TEMPLATE_MODAL: this.setShowRetireTemplateModal,
    ITEM_TABLE_DEFINITION_SET_STORED_TABLE_FAILURES: this.setStoredTableFailures,
    ITEM_TABLE_DEFINITION_SET_STORED_TABLES: this.setStoredTables,
    ITEM_TABLE_DEFINITION_SET_INPUT_FEATURE_COLUMN_IDS: this.setInputFeatureColumnIds,
  };
}
